/*
 * Copyright 2026 Sophos Limited. All rights reserved.
 *
 * 'Sophos' and 'Sophos Anti-Virus' are registered trademarks of Sophos Limited and Sophos Group. All other product
 * and company names mentioned are trademarks or registered trademarks of their respective owners.
 */
import type { DailyChecksData } from "../services/dailyChecksApi";

/**
 * Formats daily checks data as plain text suitable for pasting into a Teams message
 * @param data The daily checks data containing DLQ, errors, cloud sources, and filter stats
 */
export function formatTeamsMessage(data: DailyChecksData): string {
  const lines: string[] = [];
  const date = new Date(data.checkedAt).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  lines.push(`Date of check: ${date}`);
  lines.push("");

  if (data.dlq) {
    lines.push("DLQs:");
    if (data.dlq.items.length === 0) {
      lines.push("  No items on any DLQ");
    } else {
      for (const item of data.dlq.items) {
        lines.push(`  ${item.count} x ${item.queuePhysicalName || item.queueName}`);
      }
    }
    lines.push("");
  }

  if (data.errors) {
    lines.push("Errors:");
    if (data.errors.errors.length === 0) {
      lines.push("  No pipeline errors in the last 24h");
    } else {
      for (const err of data.errors.errors) {
        const msg = err.message.length > 120 ? err.message.slice(0, 120) + "..." : err.message;
        lines.push(`  ${err.count} x ${msg} ${err.region}`);
      }
    }
    lines.push("");
  }

  if (data.cloudSources && data.cloudSources.sources.length > 0) {
    lines.push("Cloud Sources:");
    for (const src of data.cloudSources.sources) {
      lines.push(
        `  ${src.name} - ${src.errorPercentage}% (${src.errorOrgs.toLocaleString()}/${src.totalOrgs.toLocaleString()})`,
      );
    }
    lines.push("");
  }

  if (data.filterStats && data.filterStats.topOrgs.length > 0) {
    lines.push("SOC.OS Filter Rate Limit Top Orgs:");
    lines.push("  Organisation | Event count | Region");
    for (const org of data.filterStats.topOrgs) {
      lines.push(`  ${org.organisationId} | ${org.eventCount.toLocaleString()} | ${org.region}`);
    }
  }

  return lines.join("\n");
}
